import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import "./custodian.css";
import API from "../../utils/API";
import { FEATURES } from "../../config/features";
import { useAuth } from "../../auth/AuthProvider";
import { tierCan } from "../../auth/permissions";
import { useBookData } from "../../data/BookDataProvider";
import { Button, useToast } from "../../ui";
import ContextChip from "./ContextChip";

/**
 * "Fawn's Fix-its" — the staff↔Sentinel bar (§10.7d, CUSTODIAN-UI-SPEC §A).
 *
 * A quiet strip docked to the bottom of the content area that staff toss small
 * observations to. Three laws: it never crowds the work; it is the product's
 * one character (every string is the Fawn's voice); and a note is a gift TO the
 * system — zero friction, zero obligation, no pending counts. Self-contained:
 * the shell mounts it once and nothing imports its internals.
 *
 * Send POSTs /api/questions (over-280 is trimmed server-side, never rejected);
 * receipts read /api/questions?mine=1&status=answered. FEATURES.fawnBar keeps the
 * whole surface dark until it is deliberately enabled, and it is staff-only
 * (writing+admin) even when on.
 */

const MAX = 280;
const COUNTER_AT = 200;
const THANKS_MS = 3000;
const STORE_KEY = "fawnbar.minimized";
const THANKS_LINES = [
  "Thank you — I'll look into it. 🌿", // 🌿 only on the first variant (§A4)
  "Got it. Leave it with me.",
  "Noted — I'll see what I can find."
];

function isMobileViewport() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  // Deploy finding (2026-07-16): at an unsettled mount `window.innerWidth` can be
  // 0, and 0 ≤ 640 makes matchMedia report "mobile" — wrongly defaulting a desktop
  // to the chip (the default is computed once, no resize re-check). A zero/unknown
  // width is NOT mobile; only a real, positive small width is.
  if (!window.innerWidth) {
    return false;
  }
  return window.matchMedia("(max-width: 640px)").matches;
}

function readMinimizedPref() {
  try {
    const v = window.localStorage.getItem(STORE_KEY);
    return v === "1" ? true : v === "0" ? false : null;
  } catch (e) {
    return null;
  }
}

function writeMinimizedPref(min) {
  try {
    window.localStorage.setItem(STORE_KEY, min ? "1" : "0");
  } catch (e) {
    /* private mode / storage disabled — the choice just won't persist */
  }
}

// L-3: receipts are a whisper, not a queue — once shown they clear on the NEXT
// open (E: next-open clearing, never permanent persistence). Seen ids persist
// per device, capped so the set can't grow unbounded.
const SEEN_KEY = "fawnbar.seenReceipts";
function readSeenReceipts() {
  try {
    const v = window.localStorage.getItem(SEEN_KEY);
    return v ? new Set(JSON.parse(v)) : new Set();
  } catch (e) {
    return new Set();
  }
}
function markReceiptsSeen(ids) {
  if (!ids || ids.length === 0) {
    return;
  }
  try {
    const seen = readSeenReceipts();
    ids.forEach((id) => seen.add(id));
    window.localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(seen).slice(-200)));
  } catch (e) {
    /* storage disabled — "seen" just won't persist */
  }
}

/** "Quinn Sample" → "Q. Sample" for the context chip; single names pass through. */
function shortSubjectName(name) {
  if (!name) {
    return "this record";
  }
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) {
    return parts[0];
  }
  return `${parts[0][0]}. ${parts[parts.length - 1]}`;
}

/** Rotate the thank-you line, never twice in a row (§A4). */
function pickThanksLine(lastIndex) {
  if (THANKS_LINES.length < 2) {
    return { line: THANKS_LINES[0], index: 0 };
  }
  let i = lastIndex;
  while (i === lastIndex) {
    i = Math.floor(Math.random() * THANKS_LINES.length);
  }
  return { line: THANKS_LINES[i], index: i };
}

function FawnMark({ nod, dot }) {
  return (
    <span className={`fawnbar__mark${nod ? " fawnbar__mark--nod" : ""}`}>
      <span aria-hidden="true">{"🦌"}</span>
      {dot ? <span className="fawnbar__mark-dot" aria-hidden="true" /> : null}
    </span>
  );
}

function FawnBar() {
  const { tier } = useAuth();
  const toast = useToast();
  const location = useLocation();
  const { households } = useBookData();

  const [mode, setMode] = useState("idle"); // idle | open | thanks
  const [minimized, setMinimized] = useState(() => {
    const pref = readMinimizedPref();
    return pref === null ? isMobileViewport() : pref; // small screens earn the quiet default
  });
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [thanksLine, setThanksLine] = useState(THANKS_LINES[0]);
  const [detached, setDetached] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const lastThanks = useRef(-1);
  const thanksTimer = useRef(null);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  const staff = tierCan(tier).isStaff;

  // Context auto-attach (§A3.2): on a client hub the subject is that household.
  // Drawers are in-page state — v1 attaches only the route-derived client, and
  // a free-floating (no-chip) note is first-class everywhere else.
  const clientId = useMemo(() => {
    const m = location.pathname.match(/^\/app\/clients\/([^/?#]+)/);
    return m ? m[1] : null;
  }, [location.pathname]);
  const context = useMemo(() => {
    if (!clientId) {
      return null;
    }
    const h = (households || []).find((x) => String(x.id) === String(clientId));
    return h ? { model: "Client", ref: clientId, label: `re: ${shortSubjectName(h.name)}` } : null;
  }, [clientId, households]);
  // A fresh page is a fresh context — re-attach when the client changes.
  useEffect(() => {
    setDetached(false);
  }, [clientId]);
  const attachedContext = context && !detached ? context : null;

  // Receipts (§A5) — answers coming back = the staffer's own answered questions.
  // Fetch on MOUNT (E L-1) so the minimized chip's dot can whisper before the bar
  // is ever opened; it refetches on mode change too. ?mine=1 filters server-side.
  useEffect(() => {
    if (!FEATURES.fawnBar || !staff) {
      return undefined;
    }
    let cancelled = false;
    API.getQuestions({ mine: 1, status: "answered" })
      .then((res) => {
        if (cancelled) {
          return;
        }
        const docs = Array.isArray(res.data) ? res.data : [];
        const seen = readSeenReceipts(); // L-3: don't re-whisper what was already shown
        setReceipts(
          docs
            .filter((q) => !seen.has(q._id))
            .map((q) => {
              // §A5 (E M-2): carry the answer inline so the whisper says something
              // without a click — one line; View still opens the full card.
              const answers = Array.isArray(q.answers) ? q.answers : [];
              const ans = answers.length ? answers[answers.length - 1] : null;
              const answerText = ans && (ans.text || ans.body);
              return {
                _id: q._id,
                text: answerText ? `${q.body} — answered: ${answerText}` : q.body,
                href: `/app/datafixes?q=${q._id}`
              };
            })
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [staff, mode]);

  // L-3: mark receipts seen when the bar CLOSES — not on open, which would collide
  // with the on-open refetch and hide them mid-view. The next open then clears
  // them; the whisper never nags twice for the same answer.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (wasOpen.current && mode !== "open" && receipts.length > 0) {
      markReceiptsSeen(receipts.map((r) => r._id));
    }
    wasOpen.current = mode === "open";
  }, [mode, receipts]);

  const openBar = useCallback(() => {
    setMode("open");
    window.setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
  }, []);

  const collapse = useCallback(() => {
    // Draft is preserved for the session; only the layout collapses (§A3.1).
    setMode((m) => (m === "open" ? "idle" : m));
  }, []);

  // While open: Esc or an outside click collapses (draft kept).
  useEffect(() => {
    if (mode !== "open") {
      return undefined;
    }
    const onKey = (e) => {
      if (e.key === "Escape") {
        collapse();
      }
    };
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        collapse();
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [mode, collapse]);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || sending) {
      return;
    }
    setSending(true);
    try {
      const res = await API.createQuestion({
        body: text,
        subjectModel: attachedContext ? attachedContext.model : undefined,
        subjectRef: attachedContext ? attachedContext.ref : undefined
      });
      // Accepted → clear + play the thank-you. Over-280 is TRIMMED server-side
      // (never rejected); the textarea already caps at 280, so this is
      // belt-and-suspenders — acknowledge the trim, don't restore or block.
      setDraft("");
      if (res && res.data && res.data.truncated) {
        toast.info("Trimmed to the first 280 — the Fawn has the pointer.");
      }
      const { line, index } = pickThanksLine(lastThanks.current);
      lastThanks.current = index;
      setThanksLine(line);
      setMode("thanks");
      setSending(false);
      window.clearTimeout(thanksTimer.current);
      thanksTimer.current = window.setTimeout(
        () => setMode((m) => (m === "thanks" ? "idle" : m)),
        THANKS_MS
      );
    } catch (err) {
      // Never silently eaten (§A3.4): the note stays in the box + a danger toast.
      // A 413 (over the server 280 cap) lands here too — the text is intact.
      setSending(false);
      toast.error("That didn't reach the Fawn — try again.");
    }
  }, [draft, sending, attachedContext, toast]);

  if (!FEATURES.fawnBar || !staff) {
    return null; // referral/client tier: does not exist, not hidden (§A7)
  }

  const hasReceipts = receipts.length > 0;
  const remaining = MAX - draft.length;
  const showCounter = draft.length >= COUNTER_AT;

  // Minimized → a 44px chip bottom-right; the choice persists per device (§A2).
  if (minimized) {
    return (
      <button
        type="button"
        className="fawnbar-chip"
        aria-label="Open Fawn's Fix-its"
        onClick={() => {
          setMinimized(false);
          writeMinimizedPref(false);
          openBar();
        }}
      >
        <FawnMark dot={hasReceipts} />
      </button>
    );
  }

  return (
    <div
      ref={rootRef}
      className={`fawnbar${mode === "open" ? " fawnbar--open" : ""}`}
      role="region"
      aria-label="Fawn's Fix-its"
    >
      {mode === "thanks" ? (
        <span className="fawnbar__thanks" role="status">
          <FawnMark nod />
          <span>{thanksLine}</span>
        </span>
      ) : mode === "open" ? (
        <>
          <FawnMark dot={hasReceipts} />
          <div className="fawnbar__col">
            {hasReceipts ? (
              <div className="fawnbar__receipts">
                <span className="fawnbar__receipts-cap">While you were away</span>
                {receipts.slice(0, 3).map((r) => (
                  <span className="fawnbar__receipt" key={r._id || r.id}>
                    <span className="fawnbar__receipt-text">{r.text || r.summary}</span>
                    {r.href ? (
                      <Link className="fawnbar__receipt-view" to={r.href}>
                        View
                      </Link>
                    ) : null}
                  </span>
                ))}
                {receipts.length > 3 ? (
                  <Link className="fawnbar__receipt-more" to="/app/datafixes">
                    {`…and ${receipts.length - 3} more in Data fixes →`}
                  </Link>
                ) : null}
              </div>
            ) : null}
            {attachedContext ? (
              <ContextChip label={attachedContext.label} onDetach={() => setDetached(true)} />
            ) : null}
            <textarea
              ref={inputRef}
              className="fawnbar__input"
              rows={2}
              maxLength={MAX}
              placeholder="Notice something off? Fix it, Fawn…"
              aria-label="Tell the Fawn what you noticed"
              value={draft}
              disabled={sending}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
            />
            {showCounter ? (
              <span className="fawnbar__counter">
                {remaining > 0
                  ? `${remaining} left`
                  : "Short is perfect — the Fawn just needs a pointer."}
              </span>
            ) : null}
          </div>
          <Button
            className="fawnbar__send"
            loading={sending}
            disabled={!draft.trim()}
            onClick={send}
          >
            Fix it, Fawn
          </Button>
        </>
      ) : (
        <>
          <button type="button" className="fawnbar__trigger" onClick={openBar}>
            <FawnMark dot={hasReceipts} />
            <span className="fawnbar__ph">Notice something off? Fix it, Fawn{"…"}</span>
          </button>
          <button
            type="button"
            className="fawnbar__min"
            aria-label="Minimize Fawn's Fix-its"
            onClick={() => {
              setMinimized(true);
              writeMinimizedPref(true);
              setMode("idle");
            }}
          >
            {"–"}
          </button>
        </>
      )}
    </div>
  );
}

export default FawnBar;
